import React, { useCallback, useReducer, useEffect } from 'react';
import { useDispatch, shallowEqual, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { chronosApi } from 'api';
import keyValueReducer from '@chronos/ui-toolkit/reducers/keyValueReducer';
import { setState } from '@chronos/ui-toolkit/actions';
import { delay } from '@chronos/utils';

import WarningIcon from '@chronos/components/Icons/WarningIcon';

import { mapState, initialize } from '../../slices/appSlice';

import * as S from './styled';

const initialState = {
  isLoading: false,
  formValues: {
    username: '',
    password: '',
  },
  formErrors: {},
};

function AuthPage() {
  const { isAuthorized } = useSelector(mapState, shallowEqual);
  const [state, internalDispatch] = useReducer(keyValueReducer, initialState);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthorized) {
      navigate('/');
    }
  }, [isAuthorized]);

  const onSubmit = useCallback(
    async (ev) => {
      ev.preventDefault();
      try {
        internalDispatch(
          setState({
            isLoading: true,
            formErrors: {},
          })
        );
        const { jwtToken } = await chronosApi.auth({
          body: state.formValues,
        });
        window.localStorage.setItem('JWT', jwtToken);
        dispatch(initialize());
      } catch (err) {
        log.error(err);
        // Fake delay to demostrate loading progress
        await delay(1 * 1000);
        internalDispatch(
          setState('formErrors', {
            password: 'Wrong username or password.',
          })
        );
      } finally {
        internalDispatch(
          setState({
            isLoading: false,
          })
        );
      }
    },
    [state.formValues]
  );

  return (
    <S.AuthPage>
      <S.AuthForm onSubmit={onSubmit}>
        <S.FormField>
          <S.InputField>
            <S.InputWithPlaceholder>
              <S.Input
                name="username"
                autoComplete="username"
                value={state.formValues.username}
                isValid={!state.formErrors.username}
                autoFocus
                isEmpty
                onChange={(ev) => {
                  internalDispatch(
                    setState('formValues', {
                      username: ev.target.value,
                    })
                  );
                }}
              />
              <S.Placeholder>Username</S.Placeholder>
            </S.InputWithPlaceholder>
            <S.InputBorder isValid={!state.formErrors.username} />
          </S.InputField>
        </S.FormField>

        <S.FormField>
          <S.InputField>
            <S.InputWithPlaceholder>
              <S.Input
                name="password"
                type="password"
                autoComplete="current-password"
                value={state.formValues.password}
                isValid={!state.formErrors.password}
                onChange={(ev) => {
                  internalDispatch(
                    setState('formValues', {
                      password: ev.target.value,
                    })
                  );
                }}
              />
              <S.Placeholder
                isEmpty={state.formValues.password === ''}
                isValid={!state.formErrors.password}
              >
                Password
              </S.Placeholder>
            </S.InputWithPlaceholder>
            <S.InputBorder isValid={!state.formErrors.password} />
          </S.InputField>
          {state.formErrors.password && (
            <S.Error>
              <WarningIcon size="small" />
              <S.ErrorMessage>{state.formErrors.password}</S.ErrorMessage>
            </S.Error>
          )}
        </S.FormField>

        <S.SubmitButton
          fit
          appearance="primary"
          type="submit"
          isLoading={state.isLoading}
          withIcon
        >
          Sign in
        </S.SubmitButton>
      </S.AuthForm>
    </S.AuthPage>
  );
}

AuthPage.propTypes = {};

export default AuthPage;
